import { ajvResolver } from '@hookform/resolvers/ajv'
import ListIcon from '@mui/icons-material/List'
import { Box, Grid, MenuItem, Paper, TextField } from '@mui/material'
import { FormAlert, FormHeader } from 'components'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import NavigationSchema, { INavigationFormData } from 'schema/security/navigation'
import api from 'services/api'
import { useStyles } from './styles'

interface IRouteParams {
  id: string
}

const NavigationForm: React.FC = () => {
  const [mainError, setMainError] = useState('')
  const [users, setUsers] = useState([])

  const params = useParams<IRouteParams>()
  const firstInputElement = useRef<any>(null)
  const classes = useStyles()
  const history = useHistory()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
    control,
  } = useForm<INavigationFormData>({
    resolver: ajvResolver(NavigationSchema),
    defaultValues: {
      userId: '',
      route: '',
    },
  })

  // initial load

  useEffect(() => {
    async function loadData() {
      // select Usuário

      await api
        .post('/users/select')
        .then((response) => {
          const { data } = response.data

          return data
        })
        .then((usersResult) => {
          setUsers(usersResult)
        })
        .catch((error) => {
          console.log(error)
          return error
        })
    }

    loadData()
  }, [])

  // main data load

  useEffect(() => {
    async function loadData() {
      const { id } = params

      // form data

      await api
        .get(`/navigations/${id}`)
        .then((response) => {
          const { data } = response.data

          const navigationResult = {
            id: data.id,
            userId: data.userId.id,
            navigationDate: data.navigationDate,
            route: data.route,
          }

          return navigationResult
        })
        .then((navigationResult: INavigationFormData) => {
          reset(navigationResult)
        })
        .catch((error) => {
          console.log(error)
          return error
        })
    }

    if (params.id) {
      loadData()
    }
  }, [params, params.id])

  // data save

  const onSubmit: SubmitHandler<INavigationFormData> = useCallback(async (data) => {
    const payLoad: INavigationFormData = {
      id: '',
      userId: data.userId,
      navigationDate: data.navigationDate,
      route: data.route,
    }

    if (params.id) {
      const { id } = params

      payLoad.id = id

      await api
        .put(`/navigations`, payLoad)
        .then(history.push('/navigations'))
        .catch((error) => {
          console.log(error.response?.data)
          setMainError(error.response?.data.data.name)
          return error.response?.data.data
        })
    } else {
      await api
        .post('/navigations', payLoad)
        .then(history.push('/navigations/new'))
        .then(() => reset())
        .then(() =>
          setTimeout(() => {
            firstInputElement.current.focus()
          }, 0)
        )
        .catch((error) => {
          console.log(error.response?.data)
          setMainError(error.response?.data.data.name)
          return error.response?.data.data
        })
    }
  }, [])

  const handleChange = (formField: any) => {
    setMainError('')
  }

  return (
    <Paper
      elevation={3}
      className={classes.paper}
    >
      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        data-testid='form'
      >
        <FormHeader
          title='Navegação'
          icon={ListIcon}
          backRoute='/navigations'
          showSaveButton={true}
          helpText='Nesta opção serão armazenados os históricos de navegação do usuário pelas diversas áreas da aplicação.'
        />

        <FormAlert
          setMainError={setMainError}
          mainError={mainError}
        />

        <Grid
          container
          spacing={1}
          className={mainError === '' ? classes.formContainer : classes.formContainerWithError}
        >
          <Grid
            item
            xs={12}
          >
            <TextField
              id='userId'
              label='Usuário'
              error={!!errors.userId}
              helperText={errors?.userId?.message}
              variant='outlined'
              margin='dense'
              size='small'
              fullWidth={true}
              value={`${watch().userId}`}
              select
              autoFocus
              inputRef={firstInputElement}
              {...register('userId', {
                onChange: (e) => {
                  setValue('userId', e.target.value)
                  handleChange(e)
                },
              })}
            >
              {users.map((user: any) => (
                <MenuItem
                  key={user.id}
                  value={user.id}
                >
                  {user.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid
            item
            xs={12}
          >
            <TextField
              id='navigationDate'
              label='Data'
              error={!!errors.navigationDate}
              helperText={errors?.navigationDate?.message}
              variant='outlined'
              margin='dense'
              size='small'
              fullWidth={true}
              type='date'
              InputLabelProps={{
                shrink: true,
              }}
              {...register('navigationDate', { onChange: (e) => handleChange(e) })}
            />
          </Grid>

          <Grid
            item
            xs={12}
          >
            <TextField
              id='route'
              label='Rota'
              error={!!errors.route}
              helperText={errors?.route?.message}
              variant='outlined'
              margin='dense'
              size='small'
              fullWidth={true}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                maxLength: 500,
              }}
              {...register('route', { onChange: (e) => handleChange(e) })}
            />
          </Grid>
        </Grid>
      </Box>
    </Paper>
  )
}

export default NavigationForm
